import { useCallback } from 'react'

import { getElementByXPath, getXPathForElement } from '../../helpers'

// https://stackoverflow.com/a/6969486/2344083
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export default function useKeepStyles(elementRef, keepStyles) {
  // Essentially adds '@media print' styles to the view wrapper.
  // This is needed because setting css @media print styles are not applied.
  // https://github.com/eKoopmans/html2pdf.js/issues/79
  // Should be called only once per element.
  // This will most likely be achieved by using
  // a hook like `useEffect` or passing in an id.
  const applyKeepStyles = useCallback(
    ({ element, id = window?.crypto?.randomUUID?.() || Date.now(), document = window.document, serializedStyles }) => {
      const selectedElementXpath = getXPathForElement(elementRef.current, document)
      const xpath = getXPathForElement(element, document)

      const selectedElementXpathInXpath = new RegExp(`(${escapeRegExp(selectedElementXpath)})(.*)`, 'g').exec(xpath)
      // When elements are cloned only one child is cloned until the selected element.
      // Replacing here ensures that we select the correct element.
      let path = xpath.replace(/\[\d+\]/g, '[1]')
      if (selectedElementXpathInXpath) {
        path = `${selectedElementXpathInXpath[1].replace(/\[\d+\]/g, '[1]')}${selectedElementXpathInXpath[2]}`
      }

      // Path of the styled element relative to the selected element (the one passed to
      // html2pdf's `.from()`). `null` when the styled element is not inside the selection.
      const relativePath = selectedElementXpathInXpath
        ? selectedElementXpathInXpath[2].replace(/\[\d+\]/g, '[1]')
        : null

      // keepStyles is an object of callback functions.
      // Using a key ensures that we don't duplicate callback functions.
      // It also allows for easy removal if needed.
      keepStyles.current[id] = ({ clonedDocument }) => {
        // html2pdf.js (>= 0.14) renders a clone of the selected element inside a
        // `.html2pdf__container`. When present, resolve the target relative to that
        // clone (the element that actually gets rendered) rather than against the whole
        // document, where the un-rendered original also lives and would be matched first.
        // Using xpath will ensure that even when the component is
        // changed by a developer, this will not need to be updated.
        const renderedRoot = clonedDocument.querySelector?.('.html2pdf__container')?.firstElementChild
        let clonedElement
        if (renderedRoot && relativePath !== null) {
          clonedElement = relativePath
            ? getElementByXPath(`.${relativePath}`, clonedDocument, renderedRoot)
            : renderedRoot
        } else {
          clonedElement = getElementByXPath(path, clonedDocument)
        }

        if (clonedElement) {
          let endPreviousStyle = ';'
          if (!clonedElement.style.cssText || clonedElement.style.cssText.endsWith(';')) {
            endPreviousStyle = ''
          }

          clonedElement.style.cssText += `${endPreviousStyle}${serializedStyles.styles}`
        }
      }
    },
    [elementRef, keepStyles]
  )

  return applyKeepStyles
}
